import { Controller } from "@hotwired/stimulus"
import { stimulus } from "~/init"

class KioskController extends Controller {
  static targets = [
    "vinSection", "manualSection", "loading", "loadingText", "resultsSection",
    "vinInput", "vinSpinner", "vinCarPreview", "vinCarTitle", "vinCarSub",
    "submitBtn", "mileageInput",
    "makeInput", "modelInput", "yearSelect", "engineSelect", "manualMileage",
    "carCard", "carTitle", "carSubtitle",
    "oilCard", "oilCardName", "oilCardArticle", "oilCardPrice", "oilCardBadge",
    "filterCard", "filterCardName", "filterCardArticle", "filterCardPrice", "filterCardBadge",
    "sparkCard", "sparkCardName", "sparkCardArticle", "sparkCardPrice", "sparkCardBadge",
    "nextChangeCard", "nextChangeText",
    "tipsCard", "tipsText",
    "cameraModal", "cameraVideo", "overlayCanvas", "captureCanvas",
    "cameraStatus", "captureBtn"
  ]

  static values = {
    decodeVinUrl:       String,
    recommendationsUrl: String,
    scanUrl:            String,
    csrf:               String
  }

  #stream     = null
  #overlayRaf = null
  #carData    = null   // decoded car info, cached for recommendations

  // ── VIN input: uppercase + auto-decode at 17 chars ────────────────────────
  onVinInput(e) {
    e.target.value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "")

    const vin = e.target.value
    if (vin.length === 17) {
      this.#autoDecodeVin(vin)
    } else {
      this.#clearVinPreview()
    }
  }

  // ── Auto-decode when VIN reaches 17 chars ─────────────────────────────────
  async #autoDecodeVin(vin) {
    this.#clearVinPreview()
    this.vinSpinnerTarget.classList.remove("hidden")
    this.submitBtnTarget.disabled = true

    try {
      const decoded = await this.#post(this.decodeVinUrlValue, { vin })

      if (decoded.error) {
        this.vinSpinnerTarget.classList.add("hidden")
        this.#showToast("VIN не найден в базе. Введите данные вручную.", true)
        return
      }

      this.#carData = { ...decoded }
      this.#showVinPreview(decoded)
    } catch {
      this.vinSpinnerTarget.classList.add("hidden")
      this.#showToast("Ошибка соединения. Попробуйте ещё раз.", true)
    }
  }

  #showVinPreview(car) {
    this.vinSpinnerTarget.classList.add("hidden")

    this.vinCarTitleTarget.textContent = `${car.make || ""} ${car.model || ""}`.trim()

    this.vinCarSubTarget.innerHTML = this.#carLines(car)
      .map(l => `<span class="block">${l}</span>`).join("")

    this.vinCarPreviewTarget.classList.remove("hidden")
    this.submitBtnTarget.disabled = false
    this.submitBtnTarget.classList.add("hover:bg-[#FFC300]")
  }

  #clearVinPreview() {
    this.vinCarPreviewTarget.classList.add("hidden")
    this.submitBtnTarget.disabled = true
    this.#carData = null
  }

  // ── Submit: use cached car data + mileage ─────────────────────────────────
  async submitVin() {
    if (!this.#carData) return
    const mileage = this.mileageInputTarget.value.replace(/\D/g, "")
    await this.#fetchRecommendations({ ...this.#carData, mileage })
  }

  // ── Camera: open ──────────────────────────────────────────────────────────
  async openCamera() {
    this.cameraModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this.#setCameraStatus("Запрашиваем доступ к камере…", false)
    this.captureBtnTarget.disabled = true
    this.captureBtnTarget.classList.add("opacity-40")

    if (!navigator.mediaDevices?.getUserMedia) {
      this.#setCameraStatus("⚠ Камера не поддерживается браузером.\nИспользуйте Chrome или Safari.", true)
      return
    }

    try {
      this.#stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      })
    } catch (err) {
      const msg = err.name === "NotAllowedError"
        ? "⚠ Доступ к камере запрещён.\nРазрешите доступ в настройках браузера."
        : `⚠ Не удалось открыть камеру.\n(${err.name})`
      this.#setCameraStatus(msg, true)
      return
    }

    const video = this.cameraVideoTarget
    video.srcObject = this.#stream
    try { await video.play() } catch {
      this.#setCameraStatus("⚠ Не удалось воспроизвести видео.", true)
      return
    }

    this.#setCameraStatus(null)
    this.captureBtnTarget.disabled = false
    this.captureBtnTarget.classList.remove("opacity-40")
    this.#startOverlay()
  }

  // ── Camera: close ─────────────────────────────────────────────────────────
  closeCamera() {
    this.#stopStream()
    this.cameraModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // ── Camera: capture frame, scan, then auto-decode found VIN ───────────────
  async captureFrame() {
    const video  = this.cameraVideoTarget
    const canvas = this.captureCanvasTarget
    canvas.width  = video.videoWidth  || 1280
    canvas.height = video.videoHeight || 720
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height)
    const base64 = canvas.toDataURL("image/jpeg", 0.92)

    this.closeCamera()
    this.#showLoading("Распознаём VIN с фото…")

    try {
      const data = await this.#post(this.scanUrlValue, { image: base64 })
      this.#showSection("vin")
      if (data.vin) {
        this.vinInputTarget.value = data.vin
        this.#showToast(`VIN найден: ${data.vin}`)
        this.#autoDecodeVin(data.vin)
      } else {
        this.#showToast("VIN не распознан. Введите вручную.", true)
      }
    } catch {
      this.#showSection("vin")
      this.#showToast("Ошибка распознавания. Попробуйте ещё раз.", true)
    }
  }

  // ── Manual form ───────────────────────────────────────────────────────────
  async submitManual() {
    const make          = this.makeInputTarget.value.trim()
    const model         = this.modelInputTarget.value.trim()
    const year          = this.yearSelectTarget.value
    const engine_volume = this.engineSelectTarget.value
    const mileage       = this.manualMileageTarget.value.replace(/\D/g, "")

    if (!make || !model || !year || !engine_volume) {
      this.#showToast("Заполните все обязательные поля", true)
      return
    }

    await this.#fetchRecommendations({ make, model, year, engine_volume, mileage })
  }

  showManual() { this.#showSection("manual") }
  showVin()    { this.#showSection("vin") }

  // ── Reset ─────────────────────────────────────────────────────────────────
  reset() {
    this.vinInputTarget.value      = ""
    this.mileageInputTarget.value  = ""
    this.makeInputTarget.value     = ""
    this.modelInputTarget.value    = ""
    this.yearSelectTarget.value    = ""
    this.engineSelectTarget.value  = ""
    this.manualMileageTarget.value = ""
    this.#carData = null
    this.#clearVinPreview()
    this.#showSection("vin")
    this.resultsSectionTarget.classList.add("hidden")
    this.carCardTarget.classList.add("hidden")
    ;[this.oilCardTarget, this.filterCardTarget, this.sparkCardTarget,
      this.nextChangeCardTarget, this.tipsCardTarget].forEach(el => el.classList.add("hidden"))
  }

  disconnect() { this.#stopStream() }

  // ── Private ───────────────────────────────────────────────────────────────
  async #fetchRecommendations(carData) {
    this.#showLoading("ИИ подбирает рекомендации…")

    try {
      const data = await this.#post(this.recommendationsUrlValue, carData)
      if (data.error) {
        this.#showSection("vin")
        this.#showToast("Не удалось получить рекомендации. Попробуйте ещё раз.", true)
        return
      }
      this.#renderResults(carData, data)
    } catch {
      this.#showSection("vin")
      this.#showToast("Ошибка соединения. Попробуйте ещё раз.", true)
    }
  }

  #renderResults(car, data) {
    this.carTitleTarget.textContent = `${car.make || ""} ${car.model || ""}`.trim()

    const line1 = []
    if (car.year)       line1.push(car.year + " г.")
    if (car.body_class) line1.push(car.body_class)
    if (car.doors)      line1.push(car.doors + "-дв.")
    if (car.mileage)    line1.push(Number(car.mileage).toLocaleString("ru-RU") + " км")

    const line2 = []
    if (car.engine_volume) line2.push(car.engine_volume + " л")
    if (car.cylinders)     line2.push(car.cylinders + " цил.")
    if (car.engine_type)   line2.push(car.engine_type)
    if (car.transmission)  line2.push(car.transmission)
    if (car.drive_type)    line2.push(car.drive_type)

    this.carSubtitleTarget.innerHTML = this.#carLines(car, car.mileage)
      .map(l => `<span class="block">${l}</span>`).join("")
    this.carCardTarget.classList.remove("hidden")

    this.#fillCard("oil",    data.oil)
    this.#fillCard("filter", data.filter)
    this.#fillCard("spark",  data.spark_plugs)

    if (data.next_change) {
      const { km, months } = data.next_change
      const p = []
      if (km)     p.push(`${Number(km).toLocaleString("ru-RU")} км`)
      if (months) p.push(`${months} мес.`)
      this.nextChangeTextTarget.textContent = p.join(" или ")
      this.nextChangeCardTarget.classList.remove("hidden")
    }

    if (data.tips) {
      this.tipsTextTarget.textContent = data.tips
      this.tipsCardTarget.classList.remove("hidden")
    }

    this.#showSection("results")
  }

  // Build 3–4 info lines from car data
  #carLines(car, mileage = null) {
    const line1 = []
    if (car.year)         line1.push(car.year + " г.")
    if (car.body_class)   line1.push(car.body_class)
    if (car.doors)        line1.push(car.doors + "-дв.")
    if (car.trim)         line1.push(car.trim)
    if (mileage)          line1.push(Number(mileage).toLocaleString("ru-RU") + " км")

    const line2 = []
    if (car.engine_volume)   line2.push(car.engine_volume + " л")
    if (car.cylinders)       line2.push(car.cylinders + " цил.")
    if (car.engine_type)     line2.push(car.engine_type)
    if (car.electrification) line2.push(car.electrification)
    if (car.transmission)    line2.push(car.transmission)
    if (car.drive_type)      line2.push(car.drive_type)

    const line3 = []
    if (car.manufacturer)  line3.push(car.manufacturer)
    const location = [car.plant_city, car.plant_state, car.plant_country].filter(Boolean).join(", ")
    if (location)          line3.push(location)

    return [line1, line2, line3].filter(l => l.length > 0).map(l => l.join(" · "))
  }

  #fillCard(key, rec) {
    if (!rec) return
    const nameEl  = this[`${key}CardNameTarget`]
    const artEl   = this[`${key}CardArticleTarget`]
    const priceEl = this[`${key}CardPriceTarget`]
    const badgeEl = this[`${key}CardBadgeTarget`]
    const cardEl  = this[`${key}CardTarget`]

    const item = rec.from_stock
    if (item) {
      nameEl.textContent  = item.name  || rec.general || "—"
      artEl.textContent   = item.article ? `арт. ${item.article}` : ""
      priceEl.textContent = item.price  ? `${Number(item.price).toLocaleString("ru-RU")} ₽` : ""
    } else {
      nameEl.textContent  = rec.general || "—"
      artEl.textContent   = ""
      priceEl.textContent = ""
      badgeEl.classList.remove("hidden")
    }
    cardEl.classList.remove("hidden")
  }

  #showSection(name) {
    this.loadingTarget.classList.add("hidden")
    this.vinSectionTarget.classList.toggle("hidden",     name !== "vin")
    this.manualSectionTarget.classList.toggle("hidden",  name !== "manual")
    this.resultsSectionTarget.classList.toggle("hidden", name !== "results")
  }

  #showLoading(text) {
    this.loadingTextTarget.textContent = text
    this.loadingTarget.classList.remove("hidden")
    this.vinSectionTarget.classList.add("hidden")
    this.manualSectionTarget.classList.add("hidden")
    this.resultsSectionTarget.classList.add("hidden")
  }

  #startOverlay() {
    const canvas = this.overlayCanvasTarget
    const draw = () => {
      const W = canvas.offsetWidth
      const H = canvas.offsetHeight
      if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H }

      const ctx = canvas.getContext("2d")
      ctx.clearRect(0, 0, W, H)

      const frameW = Math.min(W * 0.85, 600)
      const frameH = 80
      const frameX = (W - frameW) / 2
      const frameY = (H - frameH) / 2

      ctx.fillStyle = "rgba(0,0,0,0.62)"
      ctx.fillRect(0, 0, W, H)
      ctx.clearRect(frameX, frameY, frameW, frameH)
      ctx.strokeStyle = "#FFD600"
      ctx.lineWidth   = 2
      ctx.strokeRect(frameX, frameY, frameW, frameH)

      const bLen = 28
      ctx.lineWidth = 4
      ctx.lineCap   = "square"
      for (const [cx, cy, dx, dy] of [
        [frameX, frameY, 1, 1], [frameX + frameW, frameY, -1, 1],
        [frameX, frameY + frameH, 1, -1], [frameX + frameW, frameY + frameH, -1, -1]
      ]) {
        ctx.beginPath()
        ctx.moveTo(cx + dx * bLen, cy)
        ctx.lineTo(cx, cy)
        ctx.lineTo(cx, cy + dy * bLen)
        ctx.stroke()
      }
      this.#overlayRaf = requestAnimationFrame(draw)
    }
    this.#overlayRaf = requestAnimationFrame(draw)
  }

  #stopStream() {
    if (this.#overlayRaf) { cancelAnimationFrame(this.#overlayRaf); this.#overlayRaf = null }
    if (this.#stream) { this.#stream.getTracks().forEach(t => t.stop()); this.#stream = null }
    if (this.hasCameraVideoTarget) this.cameraVideoTarget.srcObject = null
  }

  #setCameraStatus(text, isError = false) {
    const el = this.cameraStatusTarget
    if (!text) { el.classList.add("hidden"); return }
    el.classList.remove("hidden")
    el.querySelector("[data-role='status-text']").textContent = text
    el.querySelector("[data-role='status-icon']").textContent = isError ? "⚠" : "⏳"
  }

  async #post(url, body) {
    const response = await fetch(url, {
      method:  "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfValue },
      body:    JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  }

  #showToast(message, isError = false) {
    const toast = document.createElement("div")
    toast.textContent = message
    toast.className = [
      "fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-xl text-sm font-medium shadow-lg z-[60] transition-opacity duration-300 whitespace-pre-line text-center",
      isError ? "bg-red-600 text-white" : "bg-[#FFD600] text-[#1A1A1A]"
    ].join(" ")
    document.body.appendChild(toast)
    setTimeout(() => { toast.style.opacity = "0" }, 3500)
    setTimeout(() => toast.remove(), 3900)
  }
}

stimulus.register('kiosk', KioskController)
